/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* Parent Class of all dashboard charts.
*/

import { HttpClient, HttpParams } from '@angular/common/http';
import { ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import * as Highcharts from 'highcharts';
import { first } from 'rxjs/operators';
import { MyInjector } from '@src/app/app.module';
import { uuid } from '@src/app/model/abstract-object';
import { User } from '@src/app/model/user';
import { ChosenWidget, IWidgetChart } from '@src/app/shared/dashboard/DashboardTypes';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';

export abstract class DashboardChart implements IWidgetChart {
    name: string;
    dataUrl: string;
    dataParams = new HttpParams();
    divId: string;
    dataUserId: uuid;
    host: ElementRef;
    
    readonly redColor = '#ef4444';
    readonly blueColor = '#3b82f6';
    readonly greenColor = '#059669';
    readonly amberColor = '#f59e0b';
    readonly purpleColor = '#8b5cf6';

    protected router = MyInjector.instance.get(Router);
    protected http = MyInjector.instance.get(HttpClient);
    protected currentUserSvc = MyInjector.instance.get(CurrentUserService);
    protected currentUser: User;

    private highChart: Highcharts.Chart;

    abstract title: string;

    options: { name: string, action: () => void }[] = [];

    chartBackgroundColor = 'rgba(240, 239, 238, 1)';
    chartBorderRadius = 10;
    protected width: number;
    protected height: number;

    constructor(public chosenWidget: ChosenWidget) {
        this.currentUser = this.currentUserSvc.currentUser;
        if (!this.currentUser) {
            console.warn('No user');
        }
    }

    /** For a closed request or completed action, "mine" is whoever closed it - pass resolvedById / completedById */
    getShowParms(parms: unknown, responsibleUserField = 'ownedById') {
        if (this.dataParams.has('show')) {
            const what = this.dataParams.get('show');
            if (what === 'myrequests') {
                parms[responsibleUserField] = this.currentUser.id;
            } else if (what === 'myomcs') {
                parms['omc.managerId'] = this.currentUser.id;
            }
        }
        return parms;
    }

    getTooltip() {
        return {
            pointFormat: '{series.name}: {point.y} <br/><b>{point.pct:.1f}%</b>'
        };
    }

    getChartTitle() {
        return { text: this.title, style: { fontSize: '14px', fontWeight: 'bold' } };
    }

    getSubTitle(subTitle: string) {
        //return { text: this.getSeriesName(), style: {fontSize: '12px'} }
        return { text: subTitle };
    }

    protected abstract createChartOptions(): Highcharts.Options;
    protected abstract setupData(data: unknown): void;

    protected resetChart() {
        // Override me.
    }

    public getData(divId: string = this.divId) {
        this.divId = divId;
        this.http.get(this.dataUrl, { params: this.dataParams }).pipe(first()).subscribe((data: unknown[]) => {
            this.dataUserId = data['userId'];
            this.resetChart();
            this.showChart(data, divId);
        })
    }

    protected showChart(data: unknown, divId: string) {
        this.setupData(data);
        this.highChart = Highcharts.chart(divId, (this.createChartOptions() as Highcharts.Options));
    }

    reSize() {
        this.width = this.host.nativeElement.getBoundingClientRect().width;
        this.height = this.host.nativeElement.getBoundingClientRect().height;

        if (this.highChart) {
            this.highChart.setSize(this.width, this.height);
            //this.highChart.redraw();
        }
    }
}
